/* =====================================================================
 * SelectableCard — 썸네일 + 제목 + 부가 메타 + (옵션) 칩 + 더보기 메뉴 +
 *   호버 다중선택 체크박스 를 갖춘 일반 카드.
 *
 * 문서 에이전트 랜딩 카드(`DocAgentCardShell`) 패턴을 일반화한 것. 다른
 * 갤러리 형태 화면(예: CAD 에이전트) 에서도 동일한 인터랙션을 재사용
 * 하기 위해 workspace-ui 로 끌어올렸다. 본 컴포넌트는 어떤 도메인
 * (docx/xlsx/PDF/CAD …) 에도 묶이지 않는다 — 기본은 chipLabel 이고,
 * 도메인별 색/상태가 필요한 경우 chipNode 를 전달하면 된다.
 *
 * 카드 본체 인터랙션:
 *   - 본문 영역(썸네일 포함) 클릭은 onOpen 이 처리
 *   - 우측 상단 `MoreVertical` 클릭으로 DropdownMenu (rename / download /
 *     extra / delete) 가 표시됨 — download/extra 는 prop 이 주어질 때만 추가됨
 *   - selectable=true 일 때 좌상단 hover 체크박스로 다중 선택
 *   - 인라인 제목 편집: Enter 커밋 / Esc 취소 / blur 커밋
 *
 * 디자인 일관성: 색·간격·라운드는 모두 theme 토큰만 사용한다.
 * ===================================================================== */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ArrowUpRight, Check, Download, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DropdownMenu, type DropdownMenuItem } from './DropdownMenu';
import { Chip } from './Chip';

export interface SelectableCardProps {
  /** 우측 상단 칩 라벨. undefined 면 칩 미표시. */
  chipLabel?: string;
  /**
   * chipLabel 대신 렌더할 커스텀 칩. 도메인별 색/상태 칩이 필요할 때만 사용한다.
   * 기본 chipLabel 경로는 유지해 문서에이전트 등 기존 사용처에 영향이 없다.
   */
  chipNode?: React.ReactNode;
  title: string;
  secondaryMeta?: React.ReactNode;
  thumbnail: React.ReactNode;
  onOpen: () => void;
  onRename: (nextTitle: string) => void;
  onDelete: () => void;
  /** 메뉴에 "다운로드" 항목 노출 여부 — prop 이 제공될 때만 추가된다. */
  onDownload?: () => void;
  /** "제목 수정" 시 input 의 초기값으로 사용. 기본: title. */
  editInitialValue?: string;
  /** 파일 이름 변경 UI 일 때 확장자를 유지하기 위한 suffix. */
  editableSuffix?: string;
  /**
   * 다중 선택 UI 노출 여부. true 이면 좌상단 체크박스를 렌더한다.
   * (hover 또는 선택 상태에서만 visible)
   */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelected?: (next: boolean) => void;
  /** 메뉴 진입을 일시적으로 비활성화하고 싶을 때 사용. (다운로드 없는 식) */
  disableMenu?: boolean;
  /** 도메인별 추가 메뉴 항목. 기본 rename/download/delete 흐름은 유지된다. */
  extraMenuItems?: readonly DropdownMenuItem[];
  /** Replace the open action while retaining the document card's button design. */
  actions?: readonly {
    id?: string;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    selected?: boolean;
  }[];
}

export const SelectableCard: React.FC<SelectableCardProps> = ({
  chipLabel,
  chipNode,
  title,
  secondaryMeta,
  thumbnail,
  onOpen,
  onRename,
  onDelete,
  onDownload,
  editInitialValue,
  editableSuffix,
  selectable = false,
  selected = false,
  onToggleSelected,
  disableMenu = false,
  extraMenuItems,
  actions,
}) => {
  const { t } = useTranslation('common');
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(editInitialValue ?? title);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);

  const startEdit = useCallback(() => {
    setDraft(editInitialValue ?? title);
    setIsEditing(true);
  }, [editInitialValue, title]);

  const commitEdit = useCallback(() => {
    const trimmed = draft.trim();
    setIsEditing(false);
    if (!trimmed) return;
    const baseValue = editInitialValue ?? title;
    if (trimmed === baseValue) return;
    const next = editableSuffix ? `${trimmed}${editableSuffix}` : trimmed;
    onRename(next);
  }, [draft, editInitialValue, editableSuffix, onRename, title]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setDraft(editInitialValue ?? title);
  }, [editInitialValue, title]);

  const openMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const rect = menuTriggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({ x: rect.right, y: rect.bottom + 4 });
  }, []);

  const menuItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      {
        id: 'rename',
        label: t('selectableCard.menu.rename', { defaultValue: '제목 수정' }),
        icon: <Pencil size={14} />,
        onClick: () => {
          setMenuPos(null);
          startEdit();
        },
      },
    ];
    if (onDownload) {
      items.push({
        id: 'download',
        label: t('selectableCard.menu.download', { defaultValue: '다운로드' }),
        icon: <Download size={14} />,
        onClick: () => {
          setMenuPos(null);
          onDownload();
        },
      });
    }
    if (extraMenuItems?.length) {
      items.push(
        ...extraMenuItems.map((item) => ({
          ...item,
          onClick: () => {
            setMenuPos(null);
            item.onClick();
          },
        })),
      );
    }
    items.push({
      id: 'delete',
      label: t('selectableCard.menu.delete', { defaultValue: '삭제' }),
      icon: <Trash2 size={14} />,
      danger: true,
      onClick: () => {
        setMenuPos(null);
        onDelete();
      },
    });
    return items;
  }, [extraMenuItems, onDelete, onDownload, startEdit, t]);

  const handleOpenClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isEditing) return;
      onOpen();
    },
    [isEditing, onOpen],
  );

  const handleToggleSelected = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!selectable) return;
      onToggleSelected?.(!selected);
    },
    [onToggleSelected, selectable, selected],
  );

  return (
    <CardRoot $selected={selectable && selected}>
      {selectable && (
        <SelectCheckbox
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={
            selected
              ? t('selectableCard.unselect', { defaultValue: '선택 해제' })
              : t('selectableCard.select', { defaultValue: '카드 선택' })
          }
          $visible={selected}
          $checked={selected}
          data-card-select="true"
          data-checked={selected ? 'true' : 'false'}
          onClick={handleToggleSelected}
        >
          {selected ? <Check size={14} strokeWidth={3} /> : null}
        </SelectCheckbox>
      )}
      <ThumbSlot>{thumbnail}</ThumbSlot>
      <MetaRow>
        <TitleLine>
          {isEditing ? (
            <InlineInput
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              onBlur={commitEdit}
            />
          ) : (
            <Title title={title}>{title}</Title>
          )}
          {!disableMenu && (
            <MenuButton
              ref={menuTriggerRef}
              type="button"
              aria-label="open card menu"
              onClick={openMenu}
            >
              <MoreVertical size={16} />
            </MenuButton>
          )}
          {menuPos && (
            <CardMenuHost>
              <DropdownMenu
                open
                x={menuPos.x}
                y={menuPos.y}
                items={menuItems}
                onClose={() => setMenuPos(null)}
                align="right"
                minWidth={160}
              />
            </CardMenuHost>
          )}
        </TitleLine>
        {(chipNode || chipLabel || secondaryMeta) && (
          <ChipsRow>
            {chipNode ??
              (chipLabel ? (
              <Chip size="sm" variant="subtle">
                {chipLabel}
              </Chip>
              ) : null)}
            {secondaryMeta && <SecondaryMeta>{secondaryMeta}</SecondaryMeta>}
          </ChipsRow>
        )}
      </MetaRow>
      {actions ? <ActionRow>{actions.map((action, index) => (
        <OpenButton
          key={action.id ?? index}
          type="button"
          disabled={isEditing || action.disabled}
          aria-pressed={action.selected}
          onClick={(event) => { event.stopPropagation(); action.onClick(); }}
        >
          {action.selected && <Check size={14} />}
          <span>{action.label}</span>
        </OpenButton>
      ))}</ActionRow> : <OpenButton
        type="button"
        onClick={handleOpenClick}
        disabled={isEditing}
        aria-label={t('selectableCard.open', { defaultValue: '열기' })}
      >
        <span>{t('selectableCard.open', { defaultValue: '열기' })}</span>
        <ArrowUpRight size={14} />
      </OpenButton>}
    </CardRoot>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const CardRoot = styled.div<{ $selected?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};
  text-align: left;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderInput};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 6px 14px rgba(0,0,0,0.22)'
        : '0 6px 14px rgba(0,0,0,0.06)'};
  }

  /* 선택된 카드만 항상 체크박스를 드러낸다. */
  &:hover button[data-card-select],
  & button[data-card-select][data-checked='true'] {
    opacity: 1;
    transform: scale(1);
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      border-color: var(--selectable-card-accent, ${theme.colors.accent});
      box-shadow: 0 0 0 1px var(--selectable-card-accent, ${theme.colors.accent}) inset;
    `}
`;

const SelectCheckbox = styled.button<{
  $visible: boolean;
  $checked: boolean;
}>`
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 2;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast},
    background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'scale(1)' : 'scale(0.9)')};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);

  ${({ theme, $checked }) =>
    $checked
      ? css`
          background: var(--selectable-card-accent, ${theme.colors.success});
          border: 1px solid var(--selectable-card-accent, ${theme.colors.success});
          color: var(--selectable-card-contrast, #ffffff);
        `
      : css`
          background: ${theme.mode === 'dark'
            ? 'rgba(33, 33, 33, 0.85)'
            : 'rgba(255, 255, 255, 0.95)'};
          border: 1px solid ${theme.colors.borderInput};
          color: transparent;

          &:hover {
            border-color: var(--selectable-card-accent, ${theme.colors.success});
          }
        `}
`;

const ThumbSlot = styled.div`
  width: 100%;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Title = styled.div`
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const InlineInput = styled.input`
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: 6px;
  padding: 2px 6px;
  outline: none;
  min-width: 0;
`;

const MenuButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  }
`;

const CardMenuHost = styled.span`
  position: absolute;
  width: 0;
  height: 0;
`;

const ChipsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SecondaryMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
  & > button { flex: 1 1 110px; }
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid transparent;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'};
    border-color: ${({ theme }) => theme.colors.borderInput};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &[aria-pressed='true'], &[aria-pressed='true']:hover:not(:disabled) {
    color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.brandGradientEnd : theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSoft};
    border-color: ${({ theme }) => theme.colors.brand};
  }
`;
